import { Banner } from '@sunwebgroup/uds-ui/banner'
import { TextField } from '@sunwebgroup/uds-ui/fields/text-field'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { sub } from 'date-fns'
import { isEmpty } from 'ramda'
import * as React from 'react'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { useUser } from '@/domain/user/hooks'
import { useIntl } from '@/intl'
import { CheckoutSection } from '@/scopes/checkout/components/CheckoutSection'
import { getIconForService } from '@/scopes/checkout/components/ServiceIcon'
import { useSetParticipantsBadge } from '@/scopes/checkout/domain/hooks'
import { CheckoutParticipant, ParticipantTypeValues } from '@/scopes/checkout/domain/types'
import { DateInput } from '@/scopes/common/components/FormElements/DateInput'
import { getErrorMessage } from '@/scopes/common/components/FormElements/getErrorMessage'
import { validatePattern } from '@/scopes/common/components/FormElements/TextInput'
import { useStatus } from '@/scopes/common/components/FormElements/useStatus'
import { useSendOptimizelyEvent } from '@/scopes/common/infrastructure/ab-testing/optimizely/useSendOptimizelyEvent'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { useFeatureFlag } from '@/scopes/common/infrastructure/feature-flags'
import { createAncillariesAvailableEvent } from '../../domain/analytics'
import { namePattern, useSmartFieldStatus } from '../helpers/Validations'
import styles from './ParticipantsBadgeForm.module.css'

const lengthValidationFieldName = 1
const defaultMinBirthdate = sub(new Date(), { years: 18 })
const defaultMaxBirthdate = sub(new Date(), { days: 1 })

interface FormValues {
  participantBadge: {
    id: string
    name: string
    birthdate?: string | Date
    email?: string
  }[]
}

interface FieldErrors {
  participantBadge?: Record<string, { name?: { message: string }, birthdate?: { message: string } }>
  email?: { message: string }
}

function toFormBirthdate(birthdate: string | undefined): string | Date | undefined {
  return birthdate ? new Date(birthdate) : undefined
}

function toComparableString(value: string | Date | undefined): string {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime()))
      return ''
    const y = value.getUTCFullYear()
    const m = String(value.getUTCMonth() + 1).padStart(2, '0')
    const d = String(value.getUTCDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  }
  return value ?? ''
}

function mapParticipantToFormValues(
  participant: CheckoutParticipant,
): FormValues['participantBadge'][number] {
  return {
    id: participant.id,
    name: participant.name ?? '',
    birthdate: toFormBirthdate(participant.birthdate),
    email: participant.email ?? '',
  }
}

export function ParticipantsBadgeForm() {
  const intl = useIntl()
  const { participants, rooms, setParticipantsBadge, warning } = useSetParticipantsBadge()
  const { sendEvent: sendOptimizelyEvent } = useSendOptimizelyEvent()

  const { sendEvent } = useAnalytics()
  const hasBeenSent = React.useRef(false)
  const formRef = React.useRef<HTMLFormElement>(null)
  const blurTimeoutRef = React.useRef<number | null>(null)

  const form = useForm<FormValues>({
    defaultValues: {
      participantBadge: Object.values(participants ?? {}).map(p => mapParticipantToFormValues(p)),
    },
    mode: 'onBlur',
  })

  React.useEffect(() => {
    if (hasBeenSent.current)
      return
    sendEvent(
      createAncillariesAvailableEvent({
        result: 'participantsBadge',
      }),
    )
    hasBeenSent.current = true
  }, [sendEvent, hasBeenSent])

  React.useEffect(() => {
    return () => {
      if (blurTimeoutRef.current !== null) {
        clearTimeout(blurTimeoutRef.current)
      }
    }
  }, [])

  React.useEffect(() => {
    if (!participants)
      return

    const participantList = Object.values(participants)
    const currentValues = form.getValues('participantBadge')
    const indexById = new Map(currentValues.map((v, i) => [v.id, i]))

    participantList.forEach((participant) => {
      const index = indexById.get(participant.id)
      if (index === undefined)
        return

      const currentFormValue = currentValues[index]
      const externalBirthdate = toComparableString(toFormBirthdate(participant.birthdate))
      const formBirthdateStr = toComparableString(currentFormValue.birthdate)

      if (externalBirthdate !== formBirthdateStr) {
        form.setValue(`participantBadge.${index}.birthdate`, toFormBirthdate(participant.birthdate))
      }
    })
  }, [participants, form])

  const onSubmit = (result: FormValues) => {
    setParticipantsBadge(result.participantBadge)
    sendEvent({
      event: 'Participants Badge',
      validated: true,
      description: 'Save participants badge form',
      element: 'on blur',
      result: 'save',
      interaction: true,
    })

    const hasFulfilledBirthdate = result.participantBadge.some(({ birthdate }) =>
      Boolean(typeof birthdate === 'string' ? birthdate?.trim() : birthdate),
    )
    if (hasFulfilledBirthdate) {
      sendOptimizelyEvent('participants_badge_date_fulfilled')
    }
  }

  const preventEnterKeySubmission = (e: React.KeyboardEvent<HTMLElement>) => {
    const target = e.target
    if (e.key === 'Enter' && target instanceof HTMLInputElement) {
      e.preventDefault()
    }
  }

  return (
    <CheckoutSection
      title={intl.formatMessage({ id: 'checkout_steps_participants_badge_form_title' })}
      description={intl.formatMessage({ id: 'checkout_steps_participants_badge_form_description' })}
      glyph={getIconForService('participantsBadge')}
    >
      <div className={styles.wrapper}>
        <FormProvider {...form}>
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <form
            ref={formRef}
            onBlur={() => {
              blurTimeoutRef.current = window.setTimeout(() => {
                const formElement = formRef.current
                const activeElement = document.activeElement
                if (formElement && activeElement && !formElement.contains(activeElement)) {
                  form.handleSubmit(onSubmit)()
                }
              }, 0)
            }}
            onFocus={() => {
              if (blurTimeoutRef.current !== null) {
                clearTimeout(blurTimeoutRef.current)
              }
            }}
            onKeyDown={preventEnterKeySubmission}
          >
            <div className={styles.participantFormWrapper}>
              {Object.values(rooms ?? {}).map(({ id: roomId, name: roomName, participantIds }) => (
                <div key={roomId} className={styles.participantFormWrapper}>
                  {!isEmpty(rooms) && Object.values(rooms ?? {}).length > 1 && roomName
                    ? (
                        <Typography as="p" variant="body-l-bold-tight" className={styles.roomName}>
                          {roomName}
                        </Typography>
                      )
                    : null}
                  {participantIds.map(participantId => (
                    <div key={participantId} className={styles.participantRowWrapper}>
                      <ParticipantRow participantId={participantId} />
                    </div>
                  ))}
                </div>
              ))}
              {isEmpty(rooms) && (
                <>
                  {Object.values(participants ?? {})?.map(participant => (
                    <div key={participant.id} className={styles.participantRowWrapper}>
                      <ParticipantRow participantId={participant.id} />
                    </div>
                  ))}
                </>
              )}
              <EmailFieldCaptureWrapper />
            </div>
          </form>
        </FormProvider>
        {warning && !isEmpty(warning) && (
          <Banner color="warning" size="M">
            <Banner.TextsWrapper>
              {warning.title && <Banner.Text>{warning.title}</Banner.Text>}
              {warning.text && <Banner.SupportingText innerHTML={warning.text} />}
            </Banner.TextsWrapper>
          </Banner>
        )}
      </div>
    </CheckoutSection>
  )
}

function ParticipantRow({ participantId }: { participantId: string }) {
  const intl = useIntl()
  const { participants, ageRestrictions } = useSetParticipantsBadge()
  const currentParticipant = participants ? participants[participantId] : undefined
  const participantIndex = Object.keys(participants ?? {}).findIndex(id => id === participantId)
  const currentAgeRestriction = ageRestrictions?.[participantId]

  const minBirthdate = currentAgeRestriction ? new Date(currentAgeRestriction.min) : defaultMinBirthdate
  const maxBirthdate = currentAgeRestriction ? new Date(currentAgeRestriction.max) : defaultMaxBirthdate
  const { register, getFieldState, control, setValue, trigger, formState, watch } = useFormContext()
  const errorsTyped = formState.errors as FieldErrors

  const nameFieldState = getFieldState(`participantBadge.${participantIndex}.name`, formState)
  const errorFieldName = errorsTyped?.participantBadge?.[participantIndex]?.name
  const watchedParticipantNameFieldValue = watch(`participantBadge.${participantIndex}.name`) ?? ''
  const nameFieldMatchingPattern = namePattern.test(watchedParticipantNameFieldValue ?? '')
  const dateFormat = useConfiguration('dateFormat')

  const statusParticipantFieldName = useSmartFieldStatus({
    value: watchedParticipantNameFieldValue,
    error: errorFieldName,
    isDirty: nameFieldState?.isDirty ?? false,
    fieldLengthValidation: lengthValidationFieldName,
    isSubmitting: true, // Assuming the form is always submitted on blur
    matchPattern: nameFieldMatchingPattern,
  })

  const shouldValidateName = (newValue: string) => {
    return Boolean(errorFieldName) && namePattern.test(newValue) && newValue.length >= lengthValidationFieldName
  }

  const participantIndexByAgeCategory = getAgeCategoryIndex(participantId, participants ?? {})

  if (!currentParticipant)
    return null

  return (
    <div className={styles.participantFieldsRow}>
      <input {...register(`participantBadge.${participantIndex}.id`, { value: participantId })} type="hidden" />
      <div className={styles.participantFormField}>
        <Controller
          name={`participantBadge.${participantIndex}.name`}
          control={control}
          rules={{
            pattern: {
              value: namePattern,
              message: intl.formatMessage({ id: 'checkout_steps_participants_badge_form_pattern_field' }),
            },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              onChange={(e) => {
                const newValue = e.target.value
                field.onChange(e)
                setValue(`participantBadge.${participantIndex}.name`, newValue, {
                  shouldValidate: shouldValidateName(newValue),
                })
              }}
              onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget) && field.value?.length >= lengthValidationFieldName) {
                  return trigger(`participantBadge.${participantIndex}.name`)
                }
                field.onBlur()
              }}
              label={intl.formatMessage(
                { id: 'checkout_steps_participants_badge_form_name_label' },
                {
                  id: participantIndexByAgeCategory,
                  ageCategory: intl.formatMessage({
                    id: `age_category_${currentParticipant.ageCategory.toLowerCase()}`,
                  }),
                },
              )}
              status={statusParticipantFieldName}
              feedbackText={errorsTyped?.participantBadge?.[participantIndex]?.name?.message}
              helpText={intl.formatMessage({ id: 'checkout_steps_participants_badge_form_name_help_text' })}
            />
          )}
        />
      </div>
      <div className={styles.participantFormFieldDate}>
        <DateInput
          name={`participantBadge.${participantIndex}.birthdate`}
          label={intl.formatMessage(
            { id: 'checkout_steps_participants_badge_form_birthdate_text' },
            {
              id: participantIndexByAgeCategory,
              ageCategory: intl.formatMessage({
                id: `age_category_${currentParticipant.ageCategory.toLowerCase()}_short`,
              }),
            },
          )}
          validateErrorMessage={
            currentParticipant.participantType === ParticipantTypeValues.MAIN_BOOKER
              ? intl.formatMessage({
                  id: 'checkout_steps_participants_form_field_validate_error_message_mainbooker_birthdate',
                })
              : intl.formatMessage({ id: 'checkout_steps_participants_badge_form_birthdate_validation_text' })
          }
          dateRange={{
            min: new Date(Date.UTC(minBirthdate.getFullYear(), minBirthdate.getMonth(), minBirthdate.getDate()))
              .toISOString()
              .slice(0, 10),
            max: new Date(Date.UTC(maxBirthdate.getFullYear(), maxBirthdate.getMonth(), maxBirthdate.getDate()))
              .toISOString()
              .slice(0, 10),
          }}
          dateFormat={dateFormat}
        />
      </div>
    </div>
  )
}

function EmailFieldCaptureWrapper() {
  const { isLoggedIn } = useUser()
  const [hasAccount, setHasAccount] = React.useState(false)

  React.useEffect(() => {
    setHasAccount(Boolean(localStorage.getItem('my_account_id')))
  }, [])

  const showEmailCapture = useFeatureFlag('KOS_ENABLE_EMAIL_FIELD_PARTICIPANTS_BADGE')

  if (isLoggedIn || hasAccount)
    return null

  if (!showEmailCapture)
    return null

  return <EmailCaptureField fieldName="participantBadge.[0].email" />
}

function EmailCaptureField({ fieldName }: { fieldName: string }) {
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { formState, register } = useFormContext()

  const pattern
    = '^[a-zA-Z0-9!#$%&\'*+\\/=?^_`\\{\\|\\}~\\-]+(?:\\.[a-zA-Z0-9!#$%&\'*+\\/=?^_`\\{\\|\\}~\\-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9\\-]*[a-zA-Z0-9])?\\.)+[a-zA-Z0-9](?:[a-zA-Z0-9\\-]*[a-zA-Z0-9])?$'
  const fieldLengthValidation = 1

  const validations = React.useMemo(
    () => ({
      ...(pattern ? { matchPattern: (value: any) => validatePattern(value, pattern) } : {}),
    }),
    [pattern],
  )

  const { errors } = formState
  const shouldMarkValidStatus = React.useCallback((value: string) => value?.trim().length >= fieldLengthValidation, [
    fieldLengthValidation,
  ])
  const status = useStatus({
    name: fieldName,
    required: false,
    validations,
    shouldMarkValidStatus,
  })
  const errorMessage = getErrorMessage(fieldName, errors, {
    patternErrorMessage: intl.formatMessage({ id: 'checkout_steps_participants_badge_form_email_validation_text' }),
    requiredErrorMessage: '',
  })
  const privacyLink = useConfiguration('links.privacy')
  return (
    <div className={styles.emailCaptureField}>
      <TextField
        type="email"
        label={intl.formatMessage({ id: 'checkout_steps_participants_badge_form_email_label' })}
        status={status}
        feedbackText={errorMessage}
        inputProps={register(fieldName, {
          required: false,
          validate: validations,
          setValueAs: (value: string) => (typeof value === 'string' ? value.trim() : value),
        })}
        onBlur={() => {
          sendEvent({
            event: 'Participants Badge Email',
            description: 'Email captured for abandoned cart recovery',
            element: 'email field',
            result: 'email entered',
            interaction: true,
          })
        }}
      />
      <Typography variant="body-s-regular-tight" className={styles.helpText}>
        {intl.formatMessage(
          { id: 'checkout_steps_participants_badge_form_email_help_text' },
          {
            privacyLink: (
              <a href={privacyLink} target="_blank" rel="noopener noreferrer">
                {intl.formatMessage({
                  id: 'checkout_steps_participants_badge_form_email_privacy_link',
                })}
              </a>
            ),
          },
        )}
      </Typography>
    </div>
  )
}

function getAgeCategoryIndex(
  participantId: string,
  participants: Record<string, CheckoutParticipant>,
): number | undefined {
  const participant = participants[participantId]
  if (!participant)
    return undefined

  const sameCategoryParticipants = Object.values(participants).filter(p => p.ageCategory === participant.ageCategory)

  const sortedById = sameCategoryParticipants.sort((a, b) => a.id.localeCompare(b.id))

  return sortedById.findIndex(p => p.id === participantId) + 1
}
